import {Component, useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import {addDatadogError} from '../../utils/datadog/datadog-action';

const RETRY_KEY = 'error-boundary-retry-count';
const MAX_RETRIES = 2;
const COUNTDOWN_SECONDS = 5;

// Inline styles used intentionally — CSS may not load if crash is style-related.
// Not using AntD here since this renders outside the normal app tree when crashed.
const ERROR_PAGE_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  fontFamily: 'sans-serif',
  textAlign: 'center',
  padding: '2rem',
};

/**
 * Default fallback UI shown when an unhandled error crashes the app.
 * Uses native HTML elements (no AntD) since the component tree is unmounted.
 * Tags represent: HEADER (h1), REFRESH COUNTDOWN / MESSAGE (p), REFRESH BUTTON (button).
 */
function DefaultErrorBoundaryDisplay() {
  const retryCount = parseInt(sessionStorage.getItem(RETRY_KEY) || '0', 10);
  const canAutoReload = retryCount < MAX_RETRIES;
  const [countdown, setCountdown] = useState(canAutoReload ? COUNTDOWN_SECONDS : null);

  // Auto-reload with countdown; stops after MAX_RETRIES to prevent infinite loops
  useEffect(() => {
    if (!canAutoReload) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          sessionStorage.setItem(RETRY_KEY, String(retryCount + 1));
          window.location.reload();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [canAutoReload, retryCount]);

  // Manual reload resets the retry counter so user gets fresh auto-reload attempts
  function handleManualReload() {
    sessionStorage.setItem(RETRY_KEY, '0');
    window.location.reload();
  }

  return (
    <div style={ERROR_PAGE_STYLE}>
      {/* HEADER */}
      <h1>Something went wrong</h1>

      {/* REFRESH COUNTDOWN / MESSAGE */}
      <p>An unexpected error occurred.</p>
      {canAutoReload ? (
        <p>Reloading automatically in {countdown} seconds...</p>
      ) : (
        <p>Please reload the page to try again.</p>
      )}

      {/* REFRESH BUTTON */}
      <button
        onClick={handleManualReload}
        style={{
          marginTop: '1rem',
          padding: '0.5rem 1.5rem',
          fontSize: '1rem',
          cursor: 'pointer',
        }}
      >
        Reload Now
      </button>
    </div>
  );
}

/**
 * React Error Boundary — catches unhandled JS errors in the component tree
 * and reports them to Datadog with retry context.
 *
 * Props:
 *  - name: identifies which boundary caught the error (sent to Datadog)
 *  - fallback: custom fallback UI (optional, defaults to DefaultFallback)
 *  - onError: additional error callback (optional)
 *
 * Designed for reuse: wrap any subtree with a named boundary for granular catching.
 */

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {hasError: false};
  }

  static getDerivedStateFromError() {
    return {hasError: true};
  }

  componentDidCatch(error, errorInfo) {
    const retryCount = parseInt(sessionStorage.getItem(RETRY_KEY) || '0', 10);

    // Report to Datadog with full context for filtering/alerting
    addDatadogError(error, {
      source: 'ErrorBoundary',
      boundary: this.props.name || 'unknown',
      componentStack: errorInfo?.componentStack,
      retryAttempt: retryCount,
      maxRetries: MAX_RETRIES,
      willAutoReload: retryCount < MAX_RETRIES,
      url: window.location.href,
    });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <DefaultErrorBoundaryDisplay />;
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  name: PropTypes.string,
  onError: PropTypes.func,
};
